import { useCallback, useEffect, useState } from "react";
import { loadWorkspaceState, type WorkspaceState } from "./workspace-state.ts";

type Snapshot = { avatarId: string | null; state: WorkspaceState | null; error: string | null; loading: boolean };

/** A response belongs to one selection and one request, even after a rapid A -> B -> A switch. */
export function useWorkspaceState(avatarId: string | null) {
  const [revision, setRevision] = useState(0);
  const [snapshot, setSnapshot] = useState<Snapshot>({ avatarId: null, state: null, error: null, loading: false });
  const refresh = useCallback(() => setRevision((v) => v + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setSnapshot({ avatarId, state: null, error: null, loading: Boolean(avatarId) });
    if (avatarId) {
      void loadWorkspaceState(avatarId, controller.signal).then(
        (state) => {
          if (!controller.signal.aborted) setSnapshot({ avatarId, state, error: null, loading: false });
        },
        () => {
          if (!controller.signal.aborted) setSnapshot({ avatarId, state: null, error: "Could not load saved work. Try again. If this continues, sign in again.", loading: false });
        },
      );
    }
    return () => controller.abort();
  }, [avatarId, revision]);

  // Effects run after render. Never display the previous selection for that frame.
  const current = snapshot.avatarId === avatarId
    ? snapshot
    : { state: null, error: null, loading: Boolean(avatarId) };
  return { state: current.state, loading: current.loading, error: current.error, refresh };
}
